import { useEffect, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/modal';
import { FaBuilding } from 'react-icons/fa';
import InputField from 'components/fields/InputField';
import { isValidSiren, isValidVATNumber, isValidPhoneNumber } from 'utils/verification';
import { updateCompanyInfo } from 'interfaces/autoOrder';

export interface CompanyInfo {
    deliveryAddress: string;
    deliveryCity: string;
    deliveryZipCode: string;
    billingAddress: string;
    billingCity: string;
    billingZipCode: string;
    siren: string;
    vatNumber: string;
    phone: string;
}

interface CompanyInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyInfo: CompanyInfo;
    onSaved: (data: CompanyInfo) => void;
}

const emptyInfo: CompanyInfo = {
    deliveryAddress: '',
    deliveryCity: '',
    deliveryZipCode: '',
    billingAddress: '',
    billingCity: '',
    billingZipCode: '',
    siren: '',
    vatNumber: '',
    phone: '',
};

const CompanyInfoModal = (props: CompanyInfoModalProps) => {
    const { isOpen, onClose, companyInfo, onSaved } = props;

    const [form, setForm] = useState<CompanyInfo>(emptyInfo);
    const [sameAsDelivery, setSameAsDelivery] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Phone is only ever asked here if the customer didn't already have one on file.
    const phoneAlreadySet = !!(companyInfo?.phone && companyInfo.phone.trim());

    useEffect(() => {
        if (!isOpen) return;
        const base = { ...emptyInfo, ...companyInfo };
        setForm(base);
        const billingDiffers =
            !!base.billingAddress &&
            (base.billingAddress !== base.deliveryAddress ||
                base.billingCity !== base.deliveryCity ||
                base.billingZipCode !== base.deliveryZipCode);
        setSameAsDelivery(!billingDiffers);
        setError('');
    }, [isOpen, companyInfo]);

    const set = (field: keyof CompanyInfo) => (e: any) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const deliveryComplete = !!(form.deliveryAddress && form.deliveryCity && form.deliveryZipCode);
    const billingComplete =
        sameAsDelivery || !!(form.billingAddress && form.billingCity && form.billingZipCode);
    const sirenValid = isValidSiren(form.siren);
    const vatValid = isValidVATNumber(form.vatNumber);
    const phoneValid = phoneAlreadySet || isValidPhoneNumber(form.phone);

    const canSubmit = deliveryComplete && billingComplete && sirenValid && vatValid && phoneValid;

    const handleSubmit = async () => {
        if (!canSubmit || saving) return;
        setSaving(true);
        setError('');

        const payload: CompanyInfo = {
            ...form,
            ...(sameAsDelivery && {
                billingAddress: form.deliveryAddress,
                billingCity: form.deliveryCity,
                billingZipCode: form.deliveryZipCode,
            }),
            phone: phoneAlreadySet ? companyInfo.phone : form.phone,
        };

        const res = await updateCompanyInfo(payload);
        setSaving(false);

        if (!res) {
            setError("Impossible d'enregistrer vos informations, veuillez réessayer.");
            return;
        }
        onSaved(res);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
            <ModalContent className="!bg-white !rounded-2xl !shadow-2xl !mx-4 !my-4 sm:!my-8 !max-h-[85vh]">
                <ModalHeader className="!pb-2 !pt-4 !px-4 sm:!px-5 !pr-12 sm:!pr-14">
                    <div className="flex items-start gap-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FaBuilding className="text-blue-600 text-base sm:text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">
                                Complétez vos informations
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Nécessaire pour finaliser votre première commande et établir vos factures.
                            </p>
                        </div>
                    </div>
                </ModalHeader>
                <ModalCloseButton className="!top-3 !right-3 sm:!top-4 sm:!right-4 !text-slate-400 hover:!text-slate-600 !w-7 !h-7 sm:!w-8 sm:!h-8" />

                <ModalBody className="!px-4 sm:!px-5 !pb-4 !overflow-y-auto">
                    <div className="space-y-4">
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                Adresse de livraison
                            </h4>
                            <div className="space-y-2">
                                <InputField
                                    label="Adresse*"
                                    id="deliveryAddress"
                                    value={form.deliveryAddress}
                                    onChange={set('deliveryAddress')}
                                    required
                                    inputSmall
                                />
                                <div className="grid grid-cols-2 gap-2">
                                    <InputField
                                        label="Ville*"
                                        id="deliveryCity"
                                        value={form.deliveryCity}
                                        onChange={set('deliveryCity')}
                                        required
                                        inputSmall
                                    />
                                    <InputField
                                        label="Code postal*"
                                        id="deliveryZipCode"
                                        value={form.deliveryZipCode}
                                        onChange={set('deliveryZipCode')}
                                        required
                                        inputSmall
                                    />
                                </div>
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={sameAsDelivery}
                                onChange={(e) => setSameAsDelivery(e.target.checked)}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            Adresse de facturation identique à l'adresse de livraison
                        </label>

                        {!sameAsDelivery && (
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                    Adresse de facturation
                                </h4>
                                <div className="space-y-2">
                                    <InputField
                                        label="Adresse*"
                                        id="billingAddress"
                                        value={form.billingAddress}
                                        onChange={set('billingAddress')}
                                        required
                                        inputSmall
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <InputField
                                            label="Ville*"
                                            id="billingCity"
                                            value={form.billingCity}
                                            onChange={set('billingCity')}
                                            required
                                            inputSmall
                                        />
                                        <InputField
                                            label="Code postal*"
                                            id="billingZipCode"
                                            value={form.billingZipCode}
                                            onChange={set('billingZipCode')}
                                            required
                                            inputSmall
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                Facturation électronique
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                                <InputField
                                    label="Numéro de SIREN*"
                                    id="siren"
                                    value={form.siren}
                                    onChange={set('siren')}
                                    required
                                    isInvalid={!!form.siren && !sirenValid}
                                    error="SIREN invalide (9 chiffres)"
                                    inputSmall
                                />
                                <InputField
                                    label="Numéro de TVA*"
                                    id="vatNumber"
                                    value={form.vatNumber}
                                    onChange={set('vatNumber')}
                                    placeholder="FR12345678901"
                                    required
                                    isInvalid={!!form.vatNumber && !vatValid}
                                    error="TVA invalide"
                                    inputSmall
                                />
                            </div>
                        </div>

                        {!phoneAlreadySet && (
                            <InputField
                                label="Téléphone*"
                                id="phone"
                                value={form.phone}
                                onChange={set('phone')}
                                required
                                isInvalid={!!form.phone && !isValidPhoneNumber(form.phone)}
                                error="Numéro invalide"
                                inputSmall
                            />
                        )}

                        {error && <p className="text-red-500 text-xs">{error}</p>}
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit || saving}
                        className={`w-full mt-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                            canSubmit && !saving
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        }`}
                    >
                        {saving ? 'Enregistrement...' : 'Valider et continuer'}
                    </button>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CompanyInfoModal;
